/**
 * Copyright (c) 2023 Spicy Friends Ltd.
 * All rights reserved.
 *
 * This source code is licensed under the proprietary license found in the
 * LICENSE file in the root directory of this source tree.
 */
import { InMemoryCache } from '@apollo/client-integration-tanstack-start'
import { CHAT_MESSAGES_CACHE_MAX } from '@repo/shared-common'

function mergeCursorData(existing, incoming, cursor) {
  return {
    entries: cursor ? [...existing.entries, ...incoming.entries] : [...incoming.entries],
    cursor: incoming.cursor,
    count: incoming.count,
  }
}

/**
 * The chat sidebar both pages forward and polls, so it cannot use `mergeCursorData`:
 * a poll arrives with no cursor, which would drop every page loaded after the first.
 *
 * A cursorless result is therefore treated as a refresh of the head of the list rather
 * than a replacement. Incoming rooms win, the already-loaded tail follows minus anything
 * the refresh moved up, and the deepest cursor is kept so "load more" resumes from the
 * end of the tail instead of re-fetching page two.
 */
const isDroppedChatRoom = (room, readField, keepArchived, discoverable) => {
  if (readField('deletedAt', room)) {
    return true
  }
  if (!keepArchived && readField('archivedAt', room)) {
    return true
  }
  const membership = readField('myMembership', room)
  if (!membership) {
    return false
  }
  if (readField('bannedAt', membership) || readField('removedAt', membership)) {
    return true
  }
  // A public channel the caller left still belongs on the discoverable list.
  if (!discoverable && readField('leftAt', membership)) {
    return true
  }
  if (readField('hiddenAt', membership) && !(discoverable && readField('leftAt', membership))) {
    return true
  }
  return false
}

function mergeChatRoomsData(existing, incoming, cursor, readField, keepArchived, discoverable) {
  if (cursor) {
    return {
      entries: [...(existing?.entries ?? []), ...incoming.entries],
      cursor: incoming.cursor,
      count: incoming.count,
    }
  }

  const loaded = existing?.entries ?? []
  if (loaded.length === 0) {
    return incoming
  }

  const refreshed = new Set(incoming.entries.map((room) => readField('id', room)))
  const tail = loaded.filter((room) => {
    if (refreshed.has(readField('id', room))) {
      return false
    }
    return !isDroppedChatRoom(room, readField, keepArchived, discoverable)
  })

  return {
    entries: [...incoming.entries, ...tail],
    cursor: tail.length > 0 ? existing.cursor : incoming.cursor,
    count: incoming.count,
  }
}

/**
 * A cursorless `chatMessages` result is a refresh of the newest page (initial load,
 * reconnect, or disconnected poll). Replacing `entries` would drop every older page
 * already merged in. Union by id / clientMessageId and keep newest-first order.
 */
function mergeChatMessagesData(existing, incoming, cursor, readField) {
  if (cursor) {
    return {
      entries: [...(existing?.entries ?? []), ...incoming.entries],
      cursor: incoming.cursor,
    }
  }

  const loaded = existing?.entries ?? []
  if (loaded.length === 0) {
    return incoming
  }

  const seen = new Set<string>()
  const merged: typeof loaded = []
  for (const entry of [...incoming.entries, ...loaded]) {
    const id = readField('id', entry)
    const clientMessageId = readField('clientMessageId', entry)
    const key = id ?? clientMessageId
    if (key && seen.has(key)) {
      continue
    }
    if (id) {
      seen.add(id)
    }
    if (clientMessageId) {
      seen.add(clientMessageId)
    }
    merged.push(entry)
  }

  merged.sort((left, right) => {
    const leftAt = readField('createdAt', left)
    const rightAt = readField('createdAt', right)
    const leftTime = leftAt ? new Date(leftAt).getTime() : 0
    const rightTime = rightAt ? new Date(rightAt).getTime() : 0
    if (leftTime !== rightTime) {
      return rightTime - leftTime
    }
    const leftId = String(readField('id', left) ?? '')
    const rightId = String(readField('id', right) ?? '')
    if (leftId === rightId) {
      return 0
    }
    return leftId < rightId ? 1 : -1
  })

  const capped = merged.length > CHAT_MESSAGES_CACHE_MAX ? merged.slice(0, CHAT_MESSAGES_CACHE_MAX) : merged

  return {
    entries: capped,
    cursor: existing.cursor ?? incoming.cursor,
  }
}

function mergePostData(existing, incoming, page) {
  // Negative incoming page means the data has been updated via cache and we should not merge,
  // just take the incoming data
  const result = {
    entries: page === 1 || incoming.page < 0 ? [...incoming.entries] : [...existing.entries, ...incoming.entries],
    more: incoming.more,
    page: incoming.page < 0 ? existing.page : incoming.page,
  }
  return result
}

/** Fresh cache per Apollo client instance (required for SSR isolation between requests). */
export function createApolloCache() {
  return new InMemoryCache({
    typePolicies: {
      Query: {
        fields: {
          albumSharesByAlbumId: {
            keyArgs: ['albumId'],
            merge(existing, incoming, { args: { cursor } }) {
              return mergeCursorData(existing ?? { entries: [] }, incoming, cursor)
            },
          },

          mediaList: {
            keyArgs: ['filter'],
            merge(existing, incoming, { args: { cursor } }) {
              return mergeCursorData(existing ?? { entries: [] }, incoming, cursor)
            },
          },

          commentsCursor: {
            keyArgs: ['filter', ['mediaId', 'postId', 'profileId']],
            merge(existing, incoming, { args: { cursor } }) {
              return mergeCursorData(existing ?? { entries: [] }, incoming, cursor)
            },
          },

          reviewsCursor: {
            keyArgs: [
              'filter',
              ['placeIdOrSlug', 'publicationIdOrSlug', 'vendorMarketIdOrSlug', 'vendorMarketItemIdOrSlug'],
            ],
            merge(existing, incoming, { args: { cursor } }) {
              return mergeCursorData(existing ?? { entries: [] }, incoming, cursor)
            },
          },

          likesList: {
            keyArgs: ['filter', ['profileId', 'postId', 'mediaId', 'commentId']],
            merge(existing, incoming, { args: { cursor } }) {
              return mergeCursorData(existing ?? { entries: [] }, incoming, cursor)
            },
          },

          postsPage: {
            keyArgs: ['filter', 'sortBy'],
            merge(existing, incoming, { args: { page } }) {
              return mergePostData(existing ?? { entries: [] }, incoming, page)
            },
          },

          feedPage: {
            keyArgs: ['sortBy'],
            merge(existing, incoming, { args: { cursor } }) {
              const isFirstPage = !cursor
              return {
                items: isFirstPage ? [...incoming.items] : [...(existing?.items ?? []), ...incoming.items],
                more: incoming.more,
                cursor: incoming.cursor,
              }
            },
          },

          profileReferralsCursor: {
            keyArgs: ['referrerProfileId'],
            merge(existing, incoming, { args: { cursor } }) {
              return mergeCursorData(existing ?? { entries: [] }, incoming, cursor)
            },
          },

          notificationsCursor: {
            keyArgs: ['filter', ['creatorId', 'read']],
            merge(existing, incoming, { args: { cursor } }) {
              return mergeCursorData(existing ?? { entries: [] }, incoming, cursor)
            },
          },

          adminReportCasesPage: {
            keyArgs: ['filter'],
            merge(existing, incoming, { args: { after } }) {
              return {
                entries: after ? [...(existing?.entries ?? []), ...incoming.entries] : [...incoming.entries],
                cursor: incoming.cursor,
                hasNextPage: incoming.hasNextPage,
                count: incoming.count,
              }
            },
          },

          chatRooms: {
            keyArgs: [
              'filter',
              [
                'type',
                'types',
                'search',
                'includeHidden',
                'includeArchived',
                'unreadOnly',
                'pinnedOnly',
                'hiddenOnly',
                'archivedOnly',
                'discoverable',
              ],
            ],
            merge(existing, incoming, { args: { cursor, filter }, readField }) {
              return mergeChatRoomsData(
                existing,
                incoming,
                cursor,
                readField,
                Boolean(filter?.archivedOnly || filter?.includeArchived),
                Boolean(filter?.discoverable)
              )
            },
          },

          // `fromMessageId` is part of the key so a search jump does not replace the
          // live (latest) timeline. Realtime and send still patch `{ chatRoomId }`.
          chatMessages: {
            keyArgs: ['chatRoomId', 'fromMessageId'],
            merge(existing, incoming, { args: { cursor }, readField }) {
              return mergeChatMessagesData(existing ?? { entries: [] }, incoming, cursor, readField)
            },
          },

          chatMembers: {
            keyArgs: ['chatRoomId', 'filter', ['search', 'role']],
            merge(existing, incoming, { args: { cursor } }) {
              return mergeCursorData(existing ?? { entries: [] }, incoming, cursor)
            },
          },

          chatMessageReactions: {
            keyArgs: ['messageId'],
            merge(existing, incoming, { args: { cursor } }) {
              return mergeCursorData(existing ?? { entries: [] }, incoming, cursor)
            },
          },

          chatMessageSeenBy: {
            keyArgs: ['messageId'],
            merge(existing, incoming, { args: { cursor } }) {
              return mergeCursorData(existing ?? { entries: [] }, incoming, cursor)
            },
          },

          chatChannelBrowser: {
            keyArgs: ['filter', ['search']],
            merge(existing, incoming, { args: { cursor } }) {
              return mergeCursorData(existing ?? { entries: [] }, incoming, cursor)
            },
          },

          chatMessageSearch: {
            keyArgs: ['filter', ['query', 'chatRoomId', 'senderId']],
            merge(existing, incoming, { args: { cursor } }) {
              return mergeCursorData(existing ?? { entries: [] }, incoming, cursor)
            },
          },
        },
      },
    },
  })
}
